import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useAdminUsersQuery,
  useBradManagementQuery,
  useBrandSalesManagerCreateMutation,
  useBrandSalesManagerUpdateMutation,
  useCountryQuery,
} from 'api';
import { Form, notification } from 'components';
import { ErrorMappingValueType } from 'typings';
import { isObjectEmpty } from 'utils/helpers';
import { ManagementType, SalesManagerType } from '../../index';

interface IFormData {
  salesManagerId: string;
  salesManagerAssistantId: string;
  countries: string[];
  brandSalesManagerId?: string;
}

interface IUseAddEdit {
  dataToEdit?: IFormData | undefined;
  onModalStateChange: () => void;
}

export const useAddEdit = (props: IUseAddEdit) => {
  const { dataToEdit, onModalStateChange } = props;
  const [isLoading, setIsLoading] = useState(false);
  const isEditMode = dataToEdit && Object.keys(dataToEdit)?.length;
  const [form] = Form.useForm();
  const { id } = useParams();
  const { data: countryData } = useCountryQuery();
  const { data: userData } = useAdminUsersQuery();
  const { data: brandManagement = {} as ManagementType } = useBradManagementQuery({ id });
  const [createBrandSalesManager, { isLoading: createIsLoading }] = useBrandSalesManagerCreateMutation();
  const [updateBrandSalesManager, { isLoading: updateIsLoading }] = useBrandSalesManagerUpdateMutation();
  const [notificationApi, contextHolder] = notification.useNotification();
  const disabledCountries = useMemo(
    () =>
      brandManagement?.salesManagers?.reduce((acc: string[], manager: SalesManagerType) => {
        const managerDisabledCountries =
          manager?.countryResponseList?.map((i) => i.id)?.filter((id) => !dataToEdit?.countries?.includes(id)) || [];
        return acc.concat(managerDisabledCountries);
      }, []),
    [brandManagement, dataToEdit]
  );

  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const handleSubmit = useCallback(() => {
    if (isLoading) return;
    setIsLoading(true);
    const formFields: IFormData = form.getFieldsValue();
    const isFieldsTouched =
      formFields.salesManagerId !== dataToEdit?.salesManagerId ||
      formFields.salesManagerAssistantId !== dataToEdit?.salesManagerAssistantId ||
      formFields.countries?.sort()?.join() !== dataToEdit?.countries?.sort()?.join();
    if (!isFieldsTouched) {
      setIsLoading(false);
      onModalStateChange();
      form.resetFields();
    } else {
      const payload = {
        brandId: id,
        countryIds: formFields.countries,
        salesManagerId: formFields.salesManagerId,
        salesManagerAssistantId: formFields.salesManagerAssistantId,
      };

      (isEditMode
        ? updateBrandSalesManager({ ...payload, brandSalesManagerId: dataToEdit?.brandSalesManagerId })
        : createBrandSalesManager(payload)
      )
        .then(({ data, error }: any) => {
          setIsLoading(false);
          if (data?.success) {
            openNotificationWithIcon({
              type: 'success',
              message: `Sales Manager was successfully ${isObjectEmpty(dataToEdit || {}) ? 'created' : 'updated'}.`,
            });
            onModalStateChange();
            form.resetFields();
          } else if (!error.data.success) {
            const { data = {} } = error.data;
            Object.keys(data).forEach((_key: string) => {
              openNotificationWithIcon({
                type: 'error',
                message: data[_key],
              });
            });
          } else if (error.data.error) {
            openNotificationWithIcon({
              type: 'error',
              message: 'Something went wrong!',
            });
          }
        })
        .catch((err) => {
          console.log(err, 'error');
        });
    }
  }, [
    dataToEdit,
    openNotificationWithIcon,
    id,
    onModalStateChange,
    createBrandSalesManager,
    updateBrandSalesManager,
    form,
    isEditMode,
    isLoading,
  ]);

  useEffect(() => {
    if (isEditMode) {
      form.setFieldsValue(dataToEdit);
    }
  }, [dataToEdit, isEditMode, form]);
  return {
    form,
    handleSubmit,
    countryData,
    contextHolder,
    disabledCountries,
    userData,
    isEditMode,
    isLoadingUpdate: updateIsLoading || createIsLoading,
  };
};
